import axiosInstance from './axiosInstance';

const CATEGORY_URL = '/Corrohealth/helpdesk/categories';
const SUBCATEGORY_URL = '/Corrohealth/helpdesk/subcategories';
const TICKET_URL = '/Corrohealth/helpdesk/tickets';

// ===== CATEGORY API =====

export const helpdeskCategoryApi = {
  getAll: async () => {
    const response = await axiosInstance.get(CATEGORY_URL);
    return response.data.data || [];
  },

  getActive: async () => {
    const response = await axiosInstance.get(`${CATEGORY_URL}/active`);
    return response.data.data || [];
  },

  getById: async (id) => {
    const response = await axiosInstance.get(`${CATEGORY_URL}/${id}`);
    return response.data.data;
  },

  create: async (category) => {
    const response = await axiosInstance.post(CATEGORY_URL, category);
    return response.data.data;
  },

  update: async (id, category) => {
    const response = await axiosInstance.put(`${CATEGORY_URL}/${id}`, category);
    return response.data.data;
  },

  updateStatus: async (id, status) => {
    const response = await axiosInstance.put(`${CATEGORY_URL}/${id}/status?status=${encodeURIComponent(status)}`);
    return response.data.data;
  },

  delete: async (id) => {
    const response = await axiosInstance.delete(`${CATEGORY_URL}/${id}`);
    return response.data;
  },
};

// ===== SUB-CATEGORY API =====

export const helpdeskSubCategoryApi = {
  getAll: async () => {
    const response = await axiosInstance.get(SUBCATEGORY_URL);
    return response.data.data || [];
  },

  getByCategory: async (categoryId) => {
    const response = await axiosInstance.get(`${SUBCATEGORY_URL}/category/${categoryId}`);
    return response.data.data || [];
  },

  getAllByCategory: async (categoryId) => {
    const response = await axiosInstance.get(`${SUBCATEGORY_URL}/category/${categoryId}/all`);
    return response.data.data || [];
  },

  getById: async (id) => {
    const response = await axiosInstance.get(`${SUBCATEGORY_URL}/${id}`);
    return response.data.data;
  },

  create: async (subCategory, categoryId) => {
    const response = await axiosInstance.post(`${SUBCATEGORY_URL}?categoryId=${encodeURIComponent(categoryId)}`, subCategory);
    return response.data.data;
  },

  update: async (id, subCategory, categoryId) => {
    let url = `${SUBCATEGORY_URL}/${id}`;
    if (categoryId) url += `?categoryId=${encodeURIComponent(categoryId)}`;
    const response = await axiosInstance.put(url, subCategory);
    return response.data.data;
  },

  updateStatus: async (id, status) => {
    const response = await axiosInstance.put(`${SUBCATEGORY_URL}/${id}/status?status=${encodeURIComponent(status)}`);
    return response.data.data;
  },

  delete: async (id) => {
    const response = await axiosInstance.delete(`${SUBCATEGORY_URL}/${id}`);
    return response.data;
  },
};

// ===== TICKET API =====

export const helpdeskTicketApi = {
  create: async (formData) => {
    const response = await axiosInstance.post(TICKET_URL, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
    return response.data.data;
  },

  getMyTickets: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/my`);
    return response.data.data || [];
  },

  getAll: async () => {
    const response = await axiosInstance.get(TICKET_URL);
    return response.data.data || [];
  },

  getPaginated: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page !== undefined) queryParams.append('page', params.page);
    if (params.size) queryParams.append('size', params.size);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);
    if (params.status && params.status !== 'ALL') queryParams.append('status', params.status);
    if (params.priority && params.priority !== 'ALL') queryParams.append('priority', params.priority);
    if (params.requestType && params.requestType !== 'ALL') queryParams.append('requestType', params.requestType);
    if (params.categoryName && params.categoryName !== 'ALL') queryParams.append('categoryName', params.categoryName);
    if (params.city && params.city !== 'ALL') queryParams.append('city', params.city);
    if (params.assignedTo && params.assignedTo !== 'ALL') queryParams.append('assignedTo', params.assignedTo);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.search) queryParams.append('search', params.search);
    if (params.idCardStatus && params.idCardStatus !== 'ALL') queryParams.append('idCardStatus', params.idCardStatus);
    const response = await axiosInstance.get(`${TICKET_URL}/paginated?${queryParams.toString()}`);
    return response.data.data || {};
  },

  getById: async (id) => {
    const response = await axiosInstance.get(`${TICKET_URL}/${id}`);
    return response.data.data;
  },

  assignTicket: async (id, assignData) => {
    const response = await axiosInstance.put(`${TICKET_URL}/${id}/assign`, assignData);
    return response.data.data;
  },

  updateStatus: async (id, statusData) => {
    const response = await axiosInstance.put(`${TICKET_URL}/${id}/status`, statusData);
    return response.data.data;
  },

  reopenTicket: async (id) => {
    const response = await axiosInstance.put(`${TICKET_URL}/${id}/reopen`);
    return response.data.data;
  },

  updateIdCardStatus: async (id, idCardStatus) => {
    const response = await axiosInstance.put(`${TICKET_URL}/${id}/id-card-status`, { idCardStatus });
    return response.data.data;
  },

  getIdCardStatuses: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/lookup/id-card-statuses`);
    return response.data.data || [];
  },

  addComment: async (id, commentData) => {
    const response = await axiosInstance.post(`${TICKET_URL}/${id}/comments`, commentData);
    return response.data.data;
  },

  getComments: async (id) => {
    const response = await axiosInstance.get(`${TICKET_URL}/${id}/comments`);
    return response.data.data || [];
  },

  getAttachmentUrl: (id) => {
    return `${TICKET_URL}/${id}/attachment`;
  },

  getPhotoUrl: (id) => {
    return `${TICKET_URL}/${id}/photo`;
  },

  downloadAttachment: async (id) => {
    const response = await axiosInstance.get(`${TICKET_URL}/${id}/attachment`, {
      responseType: 'blob',
    });
    return response;
  },

  downloadPhoto: async (id) => {
    const response = await axiosInstance.get(`${TICKET_URL}/${id}/photo`, {
      responseType: 'blob',
    });
    return response;
  },

  getStatistics: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/statistics`);
    return response.data.data || {};
  },

  getAnalytics: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/analytics`);
    return response.data.data || {};
  },

  // Report endpoints
  getMyTicketsReport: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/reports/my-tickets`);
    return response.data.data || [];
  },

  getAssignedToMeReport: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/reports/assigned-to-me`);
    return response.data.data || [];
  },

  getTeamReport: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/reports/team`);
    return response.data.data || [];
  },

  getIdCardReport: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/reports/id-card`);
    return response.data.data || [];
  },

  bulkUpdateStatus: async (ticketIds, status, comment) => {
    const response = await axiosInstance.put(`${TICKET_URL}/bulk-status`, {
      ticketIds,
      status,
      comment,
    });
    return response.data.data || {};
  },

  reprocessTickets: async (ticketIds, comment) => {
    const response = await axiosInstance.put(`${TICKET_URL}/reprocess`, {
      ticketIds,
      comment,
    });
    return response.data.data || {};
  },

  getByRequester: async (requesterId) => {
    const response = await axiosInstance.get(`${TICKET_URL}/by-requester/${requesterId}`);
    return response.data.data || [];
  },
};

// ===== LOOKUP API =====

export const helpdeskLookupApi = {
  getCities: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/lookup/cities`);
    return response.data.data || [];
  },

  getLocationsByCity: async (city) => {
    const response = await axiosInstance.get(`${TICKET_URL}/lookup/locations-by-city?city=${encodeURIComponent(city)}`);
    return response.data.data || [];
  },

  getAdminUsers: async (location = '', city = '') => {
    const params = new URLSearchParams();
    if (location) params.append('location', location);
    if (city) params.append('city', city);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await axiosInstance.get(`${TICKET_URL}/lookup/admins${qs}`);
    return response.data.data || [];
  },

  searchEmployees: async (search = '') => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await axiosInstance.get(`${TICKET_URL}/lookup/employees${params}`);
    return response.data.data || [];
  },

  getMyProfile: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/lookup/my-profile`);
    return response.data.data || {};
  },

  getDepartments: async () => {
    const response = await axiosInstance.get(`${TICKET_URL}/lookup/departments`);
    return response.data.data || [];
  },
};

// ===== ID CARD HISTORY API (Legacy Import) =====

const ID_CARD_HISTORY_URL = '/Corrohealth/helpdesk/id-card-history';

export const idCardHistoryApi = {
  importTwoFiles: async (idEntriesFile, usersFile) => {
    const formData = new FormData();
    formData.append('idEntriesFile', idEntriesFile);
    formData.append('usersFile', usersFile);
    const response = await axiosInstance.post(`${ID_CARD_HISTORY_URL}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 minutes — large CSV import
    });
    return response.data;
  },

  getByEmail: async (email) => {
    const response = await axiosInstance.get(`${ID_CARD_HISTORY_URL}/by-email?email=${encodeURIComponent(email)}`);
    return response.data.data || [];
  },

  search: async (query) => {
    const response = await axiosInstance.get(`${ID_CARD_HISTORY_URL}/search?q=${encodeURIComponent(query)}`);
    return response.data.data || [];
  },

  getCount: async () => {
    const response = await axiosInstance.get(`${ID_CARD_HISTORY_URL}/count`);
    return response.data.data || { total: 0 };
  },
};
